import { Slot, Slottable } from '@radix-ui/react-slot';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { createContext, forwardRef, useContext, useEffect, useId, useLayoutEffect, useRef, type AnchorHTMLAttributes, type ReactNode, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import compactNight from '../../../brand/logo/vlm-logo-compact-reverse.svg?url';
import logoDay from '../../../brand/logo/vlm-logo.svg?url';
import logoNight from '../../../brand/logo/vlm-logo-reverse.svg?url';
import markSmall from '../../../brand/logo/vlm-mark-small.svg?url';
import { cn } from './cn';
import { Tooltip, useDirection } from './tooltip';

const LOGO_RATIO = 1243.84 / 425.12; // brand/logo/vlm-logo.svg viewBox
const COMPACT_RATIO = 1243.84 / 374.67; // brand/logo/vlm-logo-compact-reverse.svg viewBox: the mark's height, so its disc is the mark

/** The full logo; the day file on light themes, the reverse file on night. Traced files from brand/logo, never redrawn. */
export function Logo({ height = 40, className }: { height?: number; className?: string }) {
  const width = Math.round(height * LOGO_RATIO);
  const alt = 'VLM — Viganium Fleet Management';
  return (
    <span className={cn('inline-flex', className)}>
      <img src={logoDay} alt={alt} width={width} height={height} className="vlm-day-only" />
      <img src={logoNight} alt={alt} width={width} height={height} className="vlm-night-only" />
    </span>
  );
}

/** The disc: the mark for small places (the rail). */
export function Mark({ size = 34, className }: { size?: number; className?: string }) {
  return <img src={markSmall} alt="VLM" width={size} height={size} className={cn('shrink-0', className)} />;
}

/** The rail (as wide as it chooses), a 52 px top bar, and the page. Below md the rail hides; the top bar carries a menu. */
export function AppShell({ rail, topBar, children }: { rail: ReactNode; topBar: ReactNode; children: ReactNode }) {
  const main = useRef<HTMLElement>(null);
  usePageScroll(main);
  return (
    <div className="grid h-dvh grid-cols-[minmax(0,1fr)] bg-canvas md:grid-cols-[auto_minmax(0,1fr)]">
      {rail}
      <div className="grid min-h-0 min-w-0 grid-rows-[52px_minmax(0,1fr)]">
        {topBar}
        <main ref={main} id="main" className="relative min-h-0 min-w-0 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}

/**
 * The page area scrolls, not the window, so the router's own scroll restoration never reaches it. A new
 * page starts at the top; Back and Forward — leaving a form page too — return to where the reader was on
 * that page; a change of filters, page or tab (the same page, another address) leaves the scroll alone.
 */
function usePageScroll(main: RefObject<HTMLElement | null>) {
  const { key, pathname } = useLocation();
  const action = useNavigationType();
  const seen = useRef(new Map<string, number>());
  const at = useRef({ key, pathname });

  useEffect(() => {
    const el = main.current;
    if (!el) return;
    const keep = () => void seen.current.set(at.current.key, el.scrollTop);
    el.addEventListener('scroll', keep, { passive: true });
    return () => el.removeEventListener('scroll', keep);
  }, [main]);

  useLayoutEffect(() => {
    const el = main.current;
    const before = at.current;
    at.current = { key, pathname };
    if (!el || before.key === key) return;
    if (action !== 'POP') {
      if (pathname !== before.pathname) el.scrollTop = 0;
      else seen.current.set(key, el.scrollTop);
      return;
    }
    // what the page shows may still be arriving: try for a few frames to reach the old place
    const top = seen.current.get(key) ?? 0;
    let frame = 0;
    let tries = 0;
    const reach = () => {
      el.scrollTop = top;
      if (Math.abs(el.scrollTop - top) > 1 && tries++ < 20) frame = requestAnimationFrame(reach);
    };
    reach();
    return () => cancelAnimationFrame(frame);
  }, [key, pathname, action, main]);
}

/** Whether the rail shows its items' names; the head, the items and the toggle read it. */
const RailExpanded = createContext(false);

export interface RailProps {
  label: string;
  /** The head: a link home around `<RailLogo />`. */
  logo: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** 240 px, every icon with its name; otherwise 60 px of icons, named by tooltips. */
  expanded?: boolean;
  /** Given, the rail ends with the button that switches between the two; the labels say what a press does. */
  onExpandedChange?: (expanded: boolean) => void;
  expandLabel?: string;
  collapseLabel?: string;
}

/**
 * The navigation on the night surface. Expanding moves no icon: the icons and the active rail keep
 * their places and the names are revealed beside them (60 px → 240 px, motion `base`, `drive`).
 */
export function Rail({ label, logo, children, footer, expanded = false, onExpandedChange, expandLabel = '', collapseLabel = '' }: RailProps) {
  const id = useId();
  return (
    <RailExpanded.Provider value={expanded}>
      <nav
        id={id}
        aria-label={label}
        // every track is pinned to the rail's width (minmax(0,1fr)): an `auto` one would grow to the hidden
        // names and push the tiles out past the rail's edge
        className={cn(
          'z-header hidden min-h-0 grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden bg-night-surface pb-3 pt-3 text-asphalt-400 transition-[width] duration-base ease-drive motion-reduce:transition-none md:grid',
          expanded ? 'w-60' : 'w-[60px]',
        )}
      >
        {/* the disc's start edge at 13 px: centred in the 60 px rail, and where the lockup's disc sits in English */}
        <div className="mb-2.5 flex h-[34px] items-center ps-[13px]">{logo}</div>
        <ul className="grid min-h-0 grid-cols-[minmax(0,1fr)] content-start gap-1.5 overflow-y-auto overflow-x-hidden px-2.5 py-0.5 [scrollbar-width:none]">{children}</ul>
        {(footer || onExpandedChange) && (
          <div className="mx-2.5 mt-2 grid grid-cols-[minmax(0,1fr)] gap-1.5 border-t border-night-line pt-2">
            {footer}
            {onExpandedChange && <RailToggle expanded={expanded} onChange={onExpandedChange} label={expanded ? collapseLabel : expandLabel} controls={id} />}
          </div>
        )}
      </nav>
    </RailExpanded.Provider>
  );
}

/**
 * The rail's head: the mark; with names, the compact lockup — its disc is the mark, in the same place
 * (in English: the logo never mirrors, so in Arabic the lockup stands at the start instead). Reverse:
 * the rail is night in both themes.
 */
export function RailLogo() {
  const expanded = useContext(RailExpanded);
  if (!expanded) return <Mark size={34} />;
  return <img src={compactNight} alt="VLM" width={Math.round(34 * COMPACT_RATIO)} height={34} className="max-w-none shrink-0" />;
}

/** A 40 px tile: the icon in a 40 px square at the start (so it never moves), the name after it. Active: red-tinted with a red start rail. */
const railTile = (active: boolean) =>
  cn(
    'relative flex h-10 w-full items-center overflow-hidden rounded-[10px] text-start no-underline transition-colors duration-quick',
    active
      ? 'bg-red-500/[0.18] text-red-300 before:absolute before:inset-y-0 before:start-0 before:w-[3px] before:bg-red-400'
      : 'text-asphalt-400 hover:bg-night-overlay hover:text-asphalt-100',
  );

/** The name: fades in once the rail has started to open, out as it closes; hidden from reading while icons alone (the item's label names it). */
function RailText({ children }: { children: ReactNode }) {
  const expanded = useContext(RailExpanded);
  return (
    <span
      className={cn(
        'min-w-0 truncate pe-3 text-body font-semibold transition-[opacity,visibility] duration-quick motion-reduce:transition-none',
        expanded ? 'visible opacity-100 delay-75' : 'invisible opacity-0',
      )}
    >
      {children}
    </span>
  );
}

export interface RailItemProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  label: string;
  icon: ReactNode;
  active?: boolean;
  /** Render the child (a router link) as the item. */
  asChild?: boolean;
}

/** Icons alone, the tooltip names it, on the reading side; with names, no tooltip. */
export const RailItem = forwardRef<HTMLAnchorElement, RailItemProps>(function RailItem({ label, icon, active = false, asChild = false, className, children, ...rest }, ref) {
  const dir = useDirection();
  const expanded = useContext(RailExpanded);
  const Comp = asChild ? Slot : 'a';
  return (
    <li>
      <Tooltip label={label} side={dir === 'rtl' ? 'left' : 'right'} disabled={expanded}>
        <Comp ref={ref} aria-label={label} aria-current={active ? 'page' : undefined} className={cn(railTile(active), className)} {...rest}>
          <span className="grid size-10 shrink-0 place-items-center">{icon}</span>
          <RailText>{label}</RailText>
          <Slottable>{children}</Slottable>
        </Comp>
      </Tooltip>
    </li>
  );
});

/** Names or icons alone; the panel icon mirrors with the rail in Arabic. */
function RailToggle({ expanded, onChange, label, controls }: { expanded: boolean; onChange: (expanded: boolean) => void; label: string; controls: string }) {
  const dir = useDirection();
  const Icon = expanded ? PanelLeftClose : PanelLeftOpen;
  return (
    <Tooltip label={label} side={dir === 'rtl' ? 'left' : 'right'} disabled={expanded}>
      <button type="button" aria-label={label} aria-expanded={expanded} aria-controls={controls} onClick={() => onChange(!expanded)} className={railTile(false)}>
        <span className="grid size-10 shrink-0 place-items-center">
          <Icon size={20} aria-hidden="true" className="rtl:-scale-x-100" />
        </span>
        <RailText>{label}</RailText>
      </button>
    </Tooltip>
  );
}

export function TopBar({ children, className }: { children: ReactNode; className?: string }) {
  return <header className={cn('z-header flex min-w-0 items-center gap-3 border-b border-line bg-surface px-3 sm:px-4', className)}>{children}</header>;
}
